import { useI18n } from 'vue-i18n'

export function useUserFormat() {
  const { t } = useI18n()

  // Get user's full name
  const getUserName = (user) => {
    if (!user) return 'N/A'
    if (typeof user === 'string') return user
    
    if (user.first_name && user.last_name && user.first_name.trim() && user.last_name.trim()) {
      return `${user.first_name.trim()} ${user.last_name.trim()}`
    } else if (user.first_name && user.first_name.trim()) {
      return user.first_name.trim()
    } else if (user.last_name && user.last_name.trim()) {
      return user.last_name.trim()
    } else if (user.full_name && user.full_name.trim()) {
      return user.full_name.trim()
    } else if (user.phone_number) {
      return user.phone_number
    } else if (user.email) {
      return user.email
    }
    
    return t('common.unknownUser')
  }

  // Get user's phone number
  const getUserPhone = (user) => {
    if (!user || typeof user === 'string') return null
    return user.phone_number || null
  }

  // Get user's email
  const getUserEmail = (user) => {
    if (!user || typeof user === 'string') return null
    return user.email || null
  }

  // Format user name for display (with HTML)
  const formatUserNameDisplay = (user) => {
    if (!user) return ''
    
    // If user is a string, return as is (for backward compatibility)
    if (typeof user === 'string') return user
    
    // If user is an object with first_name and last_name
    if (user.first_name && user.last_name && user.first_name.trim() && user.last_name.trim()) {
      const fullName = `${user.first_name.trim()} ${user.last_name.trim()}`
      if (user.phone_number) {
        return `<div class="user-name-line">${fullName}</div><div class="user-phone-line">${user.phone_number}</div>`
      }
      return `<div class="user-name-line">${fullName}</div>`
    } else if (user.first_name && user.first_name.trim()) {
      if (user.phone_number) {
        return `<div class="user-name-line">${user.first_name.trim()}</div><div class="user-phone-line">${user.phone_number}</div>`
      }
      return `<div class="user-name-line">${user.first_name.trim()}</div>`
    } else if (user.last_name && user.last_name.trim()) {
      if (user.phone_number) {
        return `<div class="user-name-line">${user.last_name.trim()}</div><div class="user-phone-line">${user.phone_number}</div>`
      }
      return `<div class="user-name-line">${user.last_name.trim()}</div>`
    } else if (user.full_name && user.full_name.trim()) {
      if (user.phone_number) {
        return `<div class="user-name-line">${user.full_name.trim()}</div><div class="user-phone-line">${user.phone_number}</div>`
      }
      return `<div class="user-name-line">${user.full_name.trim()}</div>`
    } else if (user.phone_number) {
      return `<div class="user-phone-line">${user.phone_number}</div>`
    } else if (user.email) {
      return `<div class="user-name-line">${user.email}</div>`
    }
    
    // If none of the above, return a fallback
    return `<div class="user-name-line">${t('common.unknownUser')}</div>`
  }

  // Format user name for text (without HTML)
  const formatUserName = (user) => {
    if (!user) return ''
    
    // If user is a string, return as is (for backward compatibility)
    if (typeof user === 'string') return user
    
    // If user is an object with first_name and last_name
    if (user.first_name && user.last_name && user.first_name.trim() && user.last_name.trim()) {
      const fullName = `${user.first_name.trim()} ${user.last_name.trim()}`
      if (user.phone_number) {
        return `${fullName} (${user.phone_number})`
      }
      return fullName
    } else if (user.first_name && user.first_name.trim()) {
      if (user.phone_number) {
        return `${user.first_name.trim()} (${user.phone_number})`
      }
      return user.first_name.trim()
    } else if (user.last_name && user.last_name.trim()) {
      if (user.phone_number) {
        return `${user.last_name.trim()} (${user.phone_number})`
      }
      return user.last_name.trim()
    } else if (user.full_name && user.full_name.trim()) {
      if (user.phone_number) {
        return `${user.full_name.trim()} (${user.phone_number})`
      }
      return user.full_name.trim()
    } else if (user.phone_number) {
      return user.phone_number
    } else if (user.email) {
      return user.email
    }
    
    // If none of the above, return a fallback
    return t('common.unknownUser')
  }

  // Format user for clipboard copying
  const formatUserForClipboard = (user) => {
    if (!user) return ''
    
    // If user is a string, return as is
    if (typeof user === 'string') return user
    
    // If user is an object, format it for copying
    if (user.first_name && user.last_name && user.first_name.trim() && user.last_name.trim()) {
      const fullName = `${user.first_name.trim()} ${user.last_name.trim()}`
      if (user.phone_number) {
        return `${fullName} (${user.phone_number})`
      }
      return fullName
    } else if (user.first_name && user.first_name.trim()) {
      if (user.phone_number) {
        return `${user.first_name.trim()} (${user.phone_number})`
      }
      return user.first_name.trim()
    } else if (user.last_name && user.last_name.trim()) {
      if (user.phone_number) {
        return `${user.last_name.trim()} (${user.phone_number})`
      }
      return user.last_name.trim()
    } else if (user.full_name && user.full_name.trim()) {
      if (user.phone_number) {
        return `${user.full_name.trim()} (${user.phone_number})`
      }
      return user.full_name.trim()
    } else if (user.phone_number) {
      return user.phone_number
    } else if (user.email) {
      return user.email
    }
    
    return t('common.unknownUser')
  }

  return {
    getUserName,
    getUserPhone,
    getUserEmail,
    formatUserNameDisplay,
    formatUserName,
    formatUserForClipboard
  }
}
